import {
  lessonSelectors,
  tAnswer,
  tCheckTaskType,
  useCheckTaskActions
} from 'common/modules/ReactShared';
import { FC, useMemo } from 'react';
import {
  DEFAULT_LESSON_BACKGROUND,
  getBackgroundColorIfLoaded
} from 'students/views/pages/Lessons/Lesson/LessonPage/helpers';
import Page from 'students/views/pages/Page';
import { useTypedSelector } from 'students/stores/_utils';
import bgLeft from 'students/views/shared/assets/bgLightBubbles/bgLightBubbleLeft.svg';
import bgRight from 'students/views/shared/assets/bgLightBubbles/bgLightBubbleRight.svg';
import useHeartBeatPing from 'students/views/shared/components/HeartBeat/useHeartBeatPing';
import Prompt from 'students/views/shared/components/Prompt';
import { useExitPrompt, usePromptContext } from 'students/views/shared/hooks';
import styled from 'styled-components';

import { SpinnerBlock } from 'students/views/shared/components/Spinner';
import LessonTaskTitle from '../../LessonTask/LessonTaskTitle';
import NotSupportedTask from '../../NotSupportedTask';
import { tSubjectComponentProps } from '../types';
import { checkTaskMap, tCheckComponentType } from './Tasks';

export interface ICheckSubjectComponentProps extends tSubjectComponentProps {
  task: tCheckTaskType;
  /** Picks the component for a task type; defaults to `checkTaskMap`. */
  getTaskComponent?: (type: tCheckTaskType['type']) => tCheckComponentType | undefined;
}

const getDefaultTaskComponent = (type: tCheckTaskType['type']) => checkTaskMap.get(type);

const CheckTaskContainer: FC<ICheckSubjectComponentProps> = ({
  lesson,
  task,
  taskSession,
  isLoading,
  onFinishAndNext,
  isCompleting,
  getTaskComponent = getDefaultTaskComponent
}) => {
  const TaskComponent = getTaskComponent(task.type);
  const { gradient, background } = getBackgroundColorIfLoaded(
    lesson?.color || null,
    !!lesson
  );
  const lessonSession = useTypedSelector(lessonSelectors.selectLessonSession);

  const pingAction = useHeartBeatPing();
  const { isDirty, makeDirty } = usePromptContext();

  const { onNext, onSkip } = useCheckTaskActions({
    lessonTaskSession: taskSession ?? null,
    isCompleting: isCompleting ?? false,
    onFinishAndNext(answers?: tAnswer): Promise<void> {
      makeDirty(false);

      return onFinishAndNext(answers) as Promise<void>;
    }
  });

  useExitPrompt(isDirty, pingAction);

  const previousTaskId = useMemo(() => {
    const testTaskIds = lessonSession?.summary?.test?.tasks.map(({ id }) => id);
    const currentTaskId = taskSession?.taskId ?? task.id;

    if (!testTaskIds?.length || !currentTaskId) {
      return null;
    }

    const currentTaskIndex = testTaskIds.indexOf(currentTaskId);

    if (currentTaskIndex <= 0) {
      return null;
    }

    return testTaskIds[currentTaskIndex - 1];
  }, [lessonSession?.summary?.test?.tasks, task.id, taskSession?.taskId]);

  const taskBody = useMemo(() => {
    let currentTaskBody: JSX.Element | null;
    if (isLoading) {
      currentTaskBody = (
        <SSpinnerWrapper>
          <SpinnerBlock />
        </SSpinnerWrapper>
      );
    } else {
      currentTaskBody = TaskComponent ? (
        <TaskComponent
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          task={task}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          taskSession={taskSession}
          isCompleting={!!isCompleting}
          onNext={onNext}
          onSkip={onSkip}
          previousTaskId={previousTaskId}
        />
      ) : (
        <NotSupportedTask
          onNext={onSkip}
          color={lesson?.color || DEFAULT_LESSON_BACKGROUND}
          isCompleting={!!isCompleting}
        />
      );
    }

    return currentTaskBody;
  }, [
    TaskComponent,
    onNext,
    onSkip,
    isCompleting,
    isLoading,
    lesson?.color,
    previousTaskId,
    task,
    taskSession
  ]);

  const apiBgImageURL = task.backgroundImageURL || lesson?.bgImageURL;
  const hasApiBgImage = !!apiBgImageURL;
  const bgImage = hasApiBgImage
    ? `url(${apiBgImageURL})`
    : `url(${bgLeft}), url(${bgRight})`;
  const bgSize = hasApiBgImage ? 'cover' : undefined;
  const bgPosition = hasApiBgImage ? 'center' : undefined;

  return (
    <SPage
      background={background}
      gradient={gradient}
      bgImage={bgImage}
      bgSize={bgSize}
      bgPosition={bgPosition}
      hasImageOverlay={hasApiBgImage}
      overlayColor={hasApiBgImage ? lesson?.color || undefined : undefined}
    >
      <LessonTaskTitle title={lesson?.title} />

      {taskBody}

      <Prompt />
    </SPage>
  );
};

export default CheckTaskContainer;

const SPage = styled(Page)`
  justify-content: space-between;
  padding-block-start: 3.5rem;

  @media (min-width: ${({ theme }) => theme.linguBptMd}) {
    justify-content: normal;
    padding-block-start: 0;
  }
`;

const SSpinnerWrapper = styled.div`
  flex-grow: 1;
  display: grid;
  place-content: center;
`;
